import { useState } from 'react'
import { Plus, X } from 'lucide-react'
import ColumnInput from './ColumnInput'
import './editors.css'

const OPERATORS = [
  '=', '!=', '>', '<', '>=', '<=',
  'IS NULL', 'IS NOT NULL',
  'LIKE', 'IN', 'NOT IN', 'BETWEEN',
]

const EMPTY_CONDITION = { column: '', operator: '=', value: '' }

function parseValue(raw) {
  if (!raw) return { combinator: 'AND', conditions: [] }
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    return {
      combinator: parsed.combinator || 'AND',
      conditions: Array.isArray(parsed.conditions) ? parsed.conditions : [],
    }
  } catch {
    return { combinator: 'AND', conditions: [] }
  }
}

function ConditionRowsEditor({ value, onChange, disabled, columns }) {
  const [groupInput, setGroupInput] = useState('')
  const state = parseValue(value)

  const emit = (next) => onChange(JSON.stringify(next))

  const updateCondition = (idx, key, val) => {
    const conditions = [...state.conditions]
    conditions[idx] = { ...conditions[idx], [key]: val }
    emit({ ...state, conditions })
  }

  const addCondition = () => {
    emit({ ...state, conditions: [...state.conditions, { ...EMPTY_CONDITION }] })
  }

  const removeCondition = (idx) => {
    emit({ ...state, conditions: state.conditions.filter((_, i) => i !== idx) })
  }

  const setCombinator = (c) => {
    emit({ ...state, combinator: c })
  }

  const needsValue = (op) => op !== 'IS NULL' && op !== 'IS NOT NULL'
  const isBetween = (op) => op === 'BETWEEN'

  return (
    <div>
      {state.conditions.map((cond, idx) => (
        <div key={idx} className="editor-row">
          <ColumnInput
            columns={columns}
            value={cond.column}
            onChange={(val) => updateCondition(idx, 'column', val)}
            placeholder="Column"
            disabled={disabled}
          />
          <select
            className="editor-select"
            value={cond.operator || '='}
            onChange={(e) => updateCondition(idx, 'operator', e.target.value)}
            disabled={disabled}
          >
            {OPERATORS.map((op) => (
              <option key={op} value={op}>{op}</option>
            ))}
          </select>
          {needsValue(cond.operator) && !isBetween(cond.operator) && (
            <input
              className="editor-input"
              value={cond.value || ''}
              onChange={(e) => updateCondition(idx, 'value', e.target.value)}
              placeholder="Value"
              disabled={disabled}
            />
          )}
          {isBetween(cond.operator) && (
            <>
              <input
                className="editor-input"
                value={cond.value || ''}
                onChange={(e) => updateCondition(idx, 'value', e.target.value)}
                placeholder="From"
                disabled={disabled}
                style={{ flex: '0 1 70px' }}
              />
              <span className="editor-equals">to</span>
              <input
                className="editor-input"
                value={cond.value2 || ''}
                onChange={(e) => updateCondition(idx, 'value2', e.target.value)}
                placeholder="To"
                disabled={disabled}
                style={{ flex: '0 1 70px' }}
              />
            </>
          )}
          <button
            className="editor-remove-btn"
            onClick={() => removeCondition(idx)}
            disabled={disabled}
            title="Remove condition"
          >
            <X size={12} />
          </button>
        </div>
      ))}

      {state.conditions.length > 1 && (
        <div className="editor-combinator">
          <span className="editor-combinator-label">Combine:</span>
          <button
            className={`editor-combinator-btn ${state.combinator === 'AND' ? 'active' : ''}`}
            onClick={() => setCombinator('AND')}
            disabled={disabled}
          >
            AND
          </button>
          <button
            className={`editor-combinator-btn ${state.combinator === 'OR' ? 'active' : ''}`}
            onClick={() => setCombinator('OR')}
            disabled={disabled}
          >
            OR
          </button>
        </div>
      )}

      <button
        className="editor-add-btn"
        onClick={addCondition}
        disabled={disabled}
      >
        <Plus size={12} />
        Add Condition
      </button>
    </div>
  )
}

export default ConditionRowsEditor
